import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  Truck,
  Package,
  Navigation,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Phone,
  CreditCard,
  Banknote,
  Camera,
  MessageCircle,
  AlertTriangle,
  LogOut,
  RefreshCw,
  ChevronRight,
  Send,
  User,
  Store,
  Play,
  Check,
  X,
  Locate,
  Navigation2,
  AlertCircle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { 
  requestNotificationPermission, 
  notifyNewMessage, 
  notifyNewOrder,
  playNotificationSound 
} from '../utils/notifications';
import 'leaflet/dist/leaflet.css';
import { API_URL, getWsUrl } from '../config';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Navigation arrow SVG icon - rotates based on heading
const createNavigationArrowSvg = (heading = 0) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="48" height="48" style="transform: rotate(${heading}deg);">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.3"/>
    </filter>
  </defs>
  <circle cx="32" cy="32" r="28" fill="#3B82F6" filter="url(#shadow)"/>
  <circle cx="32" cy="32" r="24" fill="#2563EB"/>
  <path d="M32 12 L44 40 L32 34 L20 40 Z" fill="white"/>
</svg>
`;

// Create navigation arrow icon factory
const createDriverIcon = (heading = 0) => L.divIcon({
  html: `<div style="width: 48px; height: 48px;">${createNavigationArrowSvg(heading)}</div>`,
  className: '',
  iconSize: [48, 48],
  iconAnchor: [24, 24],
  popupAnchor: [0, -24]
});

const deliveryIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const completedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Calculate bearing between two points
const calculateBearing = (from, to) => {
  if (!from || !to) return 0;
  const lat1 = from.lat * Math.PI / 180;
  const lat2 = to.lat * Math.PI / 180;
  const dLng = (to.lng - from.lng) * Math.PI / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const bearing = Math.atan2(y, x) * 180 / Math.PI;

  return (bearing + 360) % 360;
};

// Map component to update center, zoom and rotation based on driver movement
function MapUpdater({ center, zoom = 17, heading = 0, followDriver = true }) {
  const map = useMap();

  useEffect(() => {
    if (center && followDriver) {
      map.setView(center, zoom, { animate: true, duration: 0.5 });
    }
  }, [center, zoom, followDriver, map]);

  return null;
}

// Detect when user drags the map
function MapDragHandler({ onDrag }) {
  const map = useMap();

  useEffect(() => {
    const handleDragStart = () => {
      onDrag?.();
    };

    map.on('dragstart', handleDragStart);
    return () => {
      map.off('dragstart', handleDragStart);
    };
  }, [map, onDrag]);

  return null;
}

// Driver marker that updates position in real-time
function DriverMarker({ position, heading }) {
  const map = useMap();
  const markerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
    };
  }, [map]);

  // Update marker position and icon when they change
  useEffect(() => {
    if (!position || !position.lat || !position.lng) {
      console.log('🚫 DriverMarker: No position');
      return;
    }

    console.log('🚗 DriverMarker update - Pos:', position.lat.toFixed(6), position.lng.toFixed(6), '| Heading:', heading.toFixed(0) + '°');

    const icon = L.divIcon({
      html: `<div style="width: 48px; height: 48px; transition: transform 0.3s ease;">${createNavigationArrowSvg(heading)}</div>`,
      className: '',
      iconSize: [48, 48],
      iconAnchor: [24, 24],
      popupAnchor: [0, -24]
    });

    if (!markerRef.current) {
      console.log('🚗 Creating new marker with heading:', heading.toFixed(0) + '°');
      markerRef.current = L.marker([position.lat, position.lng], { icon })
        .addTo(map)
        .bindPopup('Tu ubicación');
    } else {
      markerRef.current.setLatLng([position.lat, position.lng]);
      markerRef.current.setIcon(icon);
    }
  }, [position, heading, map]);

  return null;
}

const DeliveryApp = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [driver, setDriver] = useState(null);
  const [sessionToken, setSessionToken] = useState(localStorage.getItem('driver_session_token') || '');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [needsBranchSelection, setNeedsBranchSelection] = useState(false);
  
  // App state
  const [view, setView] = useState('orders'); // 'orders', 'route', 'delivery', 'chat', 'history'
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [activeRoute, setActiveRoute] = useState(null);
  const [routeGeometry, setRouteGeometry] = useState([]); // Polyline coordinates for map
  const [currentDelivery, setCurrentDelivery] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [previousLocation, setPreviousLocation] = useState(null);
  const [driverHeading, setDriverHeading] = useState(0); // Direction the driver is facing
  const [loading, setLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('unknown'); // 'unknown', 'requesting', 'granted', 'denied', 'error'
  const [followingDriver, setFollowingDriver] = useState(true); // Auto-follow driver on map
  const [lastRouteRecalc, setLastRouteRecalc] = useState(null); // Last time route was recalculated
  const routeRecalcTimeoutRef = useRef(null);
  
  // Delivery completion state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [proofPhoto, setProofPhoto] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problemType, setProblemType] = useState('');
  const [problemNotes, setProblemNotes] = useState('');
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [predefinedMessages, setPredefinedMessages] = useState([]);
  const [customMessage, setCustomMessage] = useState('');

  // History state
  const [historyDeliveries, setHistoryDeliveries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  
  // Login form
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // PWA install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  // Swipe state
  const [swipeProgress, setSwipeProgress] = useState({});

  const fileInputRef = useRef(null);
  const watchIdRef = useRef(null);
  const locationPollingRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Wake Lock: keep screen on while driver is authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const requestWakeLock = async () => {
      if (!('wakeLock' in navigator)) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('[WakeLock] Screen lock prevented');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('[WakeLock] Released');
        });
      } catch (err) {
        console.log('[WakeLock] Failed:', err.message);
      }
    };

    requestWakeLock();

    // Re-acquire wake lock when app returns to foreground (OS releases it on tab hide)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // PWA: capture install prompt + detect iOS + detect SW updates
  useEffect(() => {
    const dismissed = localStorage.getItem('delivery_pwa_dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

    // Android/Chrome: capture native install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!dismissed) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS: detect Safari and show manual instructions
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome|crios|fxios/i.test(navigator.userAgent);
    if (isIos && !isStandalone && !dismissed) {
      // Small delay so the login screen renders first
      setTimeout(() => setShowIosBanner(true), 1500);
    }

    // Detect service worker update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg) return;
        const onUpdate = () => setSwUpdateAvailable(true);
        if (reg.waiting) { onUpdate(); return; }
        reg.addEventListener('updatefound', () => {
          const newSw = reg.installing;
          if (!newSw) return;
          newSw.addEventListener('statechange', () => {
            if (newSw.state === 'installed' && navigator.serviceWorker.controller) {
              onUpdate();
            }
          });
        });
      });
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      toast.success('App instalada correctamente');
    }
    setInstallPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    setShowIosBanner(false);
    localStorage.setItem('delivery_pwa_dismissed', '1');
  };

  const handleSwUpdate = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
        }
      });
    }
  };

  // Swipe to complete handler
  const handleSwipeStart = (orderId, e) => {
    const touch = e.touches ? e.touches[0] : e;
    setSwipeProgress(prev => ({
      ...prev,
      [orderId]: { startX: touch.clientX, currentX: touch.clientX, swiping: true }
    }));
  };

  const handleSwipeMove = (orderId, e) => {
    if (!swipeProgress[orderId]?.swiping) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaX = touch.clientX - swipeProgress[orderId].startX;
    if (deltaX > 0) {
      setSwipeProgress(prev => ({
        ...prev,
        [orderId]: { ...prev[orderId], currentX: touch.clientX, deltaX }
      }));
    }
  };

  const handleSwipeEnd = (orderId, order) => {
    const progress = swipeProgress[orderId];
    if (progress && progress.deltaX > 200) {
      // Swipe completed - trigger delivery
      setCurrentDelivery(order);
      setShowPhotoModal(true);
    }
    setSwipeProgress(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], swiping: false, deltaX: 0 }
    }));
  };

  // Check existing session on mount
  useEffect(() => {
    if (sessionToken) {
      checkSession();
    }
    loadPredefinedMessages();
  }, []);

  // Start location tracking when authenticated
  useEffect(() => {
    if (isAuthenticated && selectedBranch) {
      startLocationTracking();
    }
    return () => {
      // Cleanup both watchPosition and polling
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (locationPollingRef.current) {
        clearInterval(locationPollingRef.current);
        locationPollingRef.current = null;
      }
    };
  }, [isAuthenticated, selectedBranch]);

  // Cargar geometría de ruta cuando hay una ruta activa en progreso y tenemos ubicación
  useEffect(() => {
    if (activeRoute && activeRoute.status === 'in_progress' && routeGeometry.length === 0 && driverLocation) {
      loadRouteGeometry(activeRoute.id, driverLocation);
    }
  }, [activeRoute, driverLocation]);

  const checkSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/routes/active?session_token=${sessionToken}`);
      if (res.ok) {
        const data = await res.json();

        // Restore driver and branch info from session
        if (data.driver) {
          setDriver(data.driver);
        }
        if (data.branch) {
          setSelectedBranch(data.branch);
        }

        setIsAuthenticated(true);

        if (data.route) {
          setActiveRoute(data.route);
          setView('route');
        }
      } else {
        localStorage.removeItem('driver_session_token');
        setSessionToken('');
      }
    } catch (error) {
      console.error('Session check failed:', error);
    }
  };

  const loadPredefinedMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/chat/predefined`);
      if (res.ok) {
        const data = await res.json();
        setPredefinedMessages(data.driver || []);
      }
    } catch (error) {
      console.error('Failed to load predefined messages:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/drivers/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Error de autenticación');
        return;
      }
      
      setDriver(data.driver);
      setSessionToken(data.driver.session_token);
      localStorage.setItem('driver_session_token', data.driver.session_token);
      setBranches(data.branches);
      
      if (data.requires_branch_selection) {
        setNeedsBranchSelection(true);
      } else {
        setSelectedBranch(data.branches[0]);
        setIsAuthenticated(true);
        toast.success('¡Bienvenido!');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBranch = async (branch) => {
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/drivers/select-branch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: sessionToken,
          branch_id: branch.id
        })
      });
      
      if (res.ok) {
        setSelectedBranch(branch);
        setNeedsBranchSelection(false);
        setIsAuthenticated(true);
        toast.success(`Trabajando en: ${branch.name}`);
      }
    } catch (error) {
      toast.error('Error al seleccionar sucursal');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/v1/delivery/drivers/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken })
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('driver_session_token');
    setIsAuthenticated(false);
    setDriver(null);
    setSessionToken('');
    setSelectedBranch(null);
    setActiveRoute(null);
  };

  const loadPendingOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/orders/pending?session_token=${sessionToken}`);
      if (res.ok) {
        const data = await res.json();
        setPendingOrders(data.orders || []);
      }
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/history/driver?session_token=${sessionToken}&limit=100`);
      if (res.ok) {
        const data = await res.json();
        setHistoryDeliveries(data.deliveries || []);
      }
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadActiveRoute = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/routes/active?session_token=${sessionToken}`);
      if (res.ok) {
        const data = await res.json();
        if (data.route) {
          setActiveRoute(data.route);
          setView('route');
          // Load route geometry if route is in progress
          if (data.route.status === 'in_progress') {
            loadRouteGeometry(data.route.id);
          }
        }
        return data.route;
      }
    } catch (error) {
      console.error('Failed to load active route:', error);
    }
    return null;
  };

  const loadRouteGeometry = async (routeId, forceLocation = null) => {
    const locationToUse = forceLocation || driverLocation;

    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/routes/${routeId}/geometry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: sessionToken,
          driver_location: locationToUse
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.geometry && data.geometry.length > 0) {
          setRouteGeometry(data.geometry);
          setLastRouteRecalc(Date.now());
        }
      }
    } catch (error) {
      console.error('Failed to load route geometry:', error);
    }
  };

  // Check if driver has deviated from the route
  const checkRouteDeviation = (currentLocation) => {
    if (!routeGeometry || routeGeometry.length < 2 || !currentLocation) return false;

    // Find minimum distance from current location to any point on the route
    let minDistance = Infinity;
    for (const point of routeGeometry) {
      const distance = Math.sqrt(
        Math.pow((currentLocation.lat - point[0]) * 111000, 2) +
        Math.pow((currentLocation.lng - point[1]) * 111000 * Math.cos(currentLocation.lat * Math.PI / 180), 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
      }
    }

    // If driver is more than 100 meters from the route, consider it a deviation
    return minDistance > 100;
  };

  // Recalculate route from current position
  const recalculateRoute = async (manual = false) => {
    if (!activeRoute || activeRoute.status !== 'in_progress') {
      toast.error('No hay ruta activa');
      return;
    }

    if (!driverLocation) {
      toast.error('Esperando ubicación GPS...');
      requestGpsPermission();
      return;
    }

    // Prevent too frequent auto-recalculations (minimum 30 seconds)
    // But allow manual recalculations anytime
    if (!manual && lastRouteRecalc && Date.now() - lastRouteRecalc < 30000) {
      return;
    }

    toast.info('Recalculando ruta desde tu ubicación...');
    await loadRouteGeometry(activeRoute.id, driverLocation);
    toast.success('Ruta actualizada');
  };

  useEffect(() => {
    if (isAuthenticated && selectedBranch && view === 'orders') {
      loadPendingOrders();
    }
  }, [isAuthenticated, selectedBranch, view]);

  // Load active route when authenticated
  useEffect(() => {
    if (isAuthenticated && sessionToken) {
      loadActiveRoute();
    }
  }, [isAuthenticated, sessionToken]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const createRoute = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Selecciona al menos un pedido');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/routes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: sessionToken,
          order_ids: selectedOrders
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setActiveRoute(data);
        setSelectedOrders([]);
        setRouteGeometry([]); // Clear geometry until route is started
        setView('route');
        toast.success('Ruta creada');
      } else {
        // Show specific error message from backend
        toast.error(data.detail || 'Error al crear ruta');
      }
    } catch (error) {
      console.error('Create route error:', error);
      toast.error('Error de conexión. Verifica tu internet.');
    } finally {
      setLoading(false);
    }
  };

  const startRoute = async () => {
    if (!activeRoute) return;

    setLoading(true);
    try {
      // Get current location before starting
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setDriverLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (error) => console.error('Error getting location:', error),
          { enableHighAccuracy: true }
        );
      }

      const res = await fetch(`${API_URL}/api/v1/delivery/routes/${activeRoute.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken })
      });

      if (res.ok) {
        await loadActiveRoute();
        // Load route geometry after starting
        await loadRouteGeometry(activeRoute.id);
        toast.success('¡Ruta iniciada! Buena suerte');
      }
    } catch (error) {
      toast.error('Error al iniciar ruta');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    const endpoints = {
      'picked_up': 'picked-up',
      'in_transit': 'in-transit',
      'nearby': 'nearby'
    };
    
    try {
      const res = await fetch(
        `${API_URL}/api/v1/delivery/routes/${activeRoute.id}/orders/${orderId}/${endpoints[status]}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_token: sessionToken })
        }
      );
      
      if (res.ok) {
        await loadActiveRoute();
        toast.success('Estado actualizado');
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const completeDelivery = async () => {
    if (!proofPhoto) {
      toast.error('Toma una foto de comprobante');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/v1/delivery/routes/${activeRoute.id}/orders/${currentDelivery.order_id}/deliver`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_token: sessionToken,
            proof_photo: proofPhoto,
            notes: deliveryNotes
          })
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        setShowPhotoModal(false);
        setProofPhoto(null);
        setDeliveryNotes('');
        setCurrentDelivery(null);

        // Check if route is completed
        if (data.route_completed) {
          toast.success('🎉 ¡Ruta completada! Todas las entregas finalizadas');
          setRouteGeometry([]);
          setActiveRoute(null);
          setView('orders');
          // Reload pending orders for next route
          await loadPendingOrders();
        } else {
          toast.success('¡Entrega completada! Siguiente en la lista');
          const route = await loadActiveRoute();
          // Reload geometry for remaining deliveries
          if (route && route.status === 'in_progress') {
            await loadRouteGeometry(route.id);
            // Auto-select next delivery
            const nextDelivery = route.orders.find(o =>
              o.status !== 'delivered' && o.status !== 'failed'
            );
            if (nextDelivery) {
              setCurrentDelivery(nextDelivery);
              setView('delivery');
            }
          } else {
            setRouteGeometry([]);
          }
        }
      }
    } catch (error) {
      toast.error('Error al completar entrega');
    } finally {
      setLoading(false);
    }
  };

  const reportProblem = async () => {
    if (!problemType) {
      toast.error('Selecciona el tipo de problema');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/v1/delivery/routes/${activeRoute.id}/orders/${currentDelivery.order_id}/problem`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_token: sessionToken,
            problem_type: problemType,
            problem_notes: problemNotes
          })
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        setShowProblemModal(false);
        setProblemType('');
        setProblemNotes('');
        setCurrentDelivery(null);

        // Check if route is completed
        if (data.route_completed) {
          toast.success('Ruta completada');
          setRouteGeometry([]);
          setActiveRoute(null);
          setView('orders');
          await loadPendingOrders();
        } else {
          toast.success('Problema reportado. Siguiente entrega');
          const route = await loadActiveRoute();
          if (route && route.status === 'in_progress') {
            await loadRouteGeometry(route.id);
            // Auto-select next delivery
            const nextDelivery = route.orders.find(o =>
              o.status !== 'delivered' && o.status !== 'failed'
            );
            if (nextDelivery) {
              setCurrentDelivery(nextDelivery);
              setView('delivery');
            }
          } else {
            setRouteGeometry([]);
          }
        }
      }
    } catch (error) {
      toast.error('Error al reportar problema');
    } finally {
      setLoading(false);
    }
  };

  // Check and request GPS permission
  const checkGpsPermission = async () => {
    if (!('geolocation' in navigator)) {
      setGpsStatus('error');
      toast.error('Tu dispositivo no soporta GPS');
      return false;
    }

    // Check if permissions API is available
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state === 'granted') {
          setGpsStatus('granted');
          return true;
        } else if (permission.state === 'denied') {
          setGpsStatus('denied');
          toast.error('GPS denegado. Activa la ubicación en configuración del navegador.');
          return false;
        } else {
          // prompt - will ask when we try to use it
          setGpsStatus('requesting');
          return true;
        }
      } catch (e) {
        // permissions API not fully supported, continue anyway
        return true;
      }
    }
    return true;
  };

  // Request GPS permission and get current location
  const requestGpsPermission = async () => {
    setGpsStatus('requesting');

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsStatus('granted');
          setDriverLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          toast.success('GPS activado correctamente');
          resolve(true);
        },
        (error) => {
          console.error('GPS permission error:', error);
          if (error.code === error.PERMISSION_DENIED) {
            setGpsStatus('denied');
            toast.error('Permiso de GPS denegado. Actívalo en la configuración del navegador.');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            setGpsStatus('error');
            toast.error('No se puede obtener la ubicación. Verifica que el GPS esté activo.');
          } else if (error.code === error.TIMEOUT) {
            setGpsStatus('error');
            toast.error('Tiempo de espera agotado. Intenta de nuevo.');
          }
          resolve(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  const startLocationTracking = async () => {
    if (!('geolocation' in navigator)) {
      setGpsStatus('error');
      toast.error('Tu dispositivo no soporta GPS');
      return;
    }

    // First, try to get current position to trigger permission request
    const hasPermission = await checkGpsPermission();
    if (!hasPermission && gpsStatus === 'denied') {
      return;
    }

    // Clear any existing watch
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    // Get initial position first
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsStatus('granted');
        const initialLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setDriverLocation(initialLocation);
      },
      (error) => {
        console.error('Initial position error:', error);
        if (error.code === error.PERMISSION_DENIED) {
          setGpsStatus('denied');
          toast.error('Activa el GPS para usar la app de repartidor');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );

    // Start continuous tracking
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setGpsStatus('granted');
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        console.log('📍 GPS Update:', newLocation.lat.toFixed(6), newLocation.lng.toFixed(6));

        // Update driver location directly (not with functional update to ensure re-render)
        setDriverLocation(currentLocation => {
          if (currentLocation) {
            // Calculate distance moved
            const distance = Math.sqrt(
              Math.pow((newLocation.lat - currentLocation.lat) * 111000, 2) +
              Math.pow((newLocation.lng - currentLocation.lng) * 111000 * Math.cos(currentLocation.lat * Math.PI / 180), 2)
            );

            // Only update heading if moved at least 5 meters
            if (distance > 5) {
              const newHeading = calculateBearing(currentLocation, newLocation);
              setDriverHeading(newHeading);
              setPreviousLocation(currentLocation);
              console.log('📍 Moved', distance.toFixed(1), 'm, heading:', newHeading.toFixed(0));
            }
          }
          // Always return new location object to trigger re-render
          return { ...newLocation };
        });

        // Use device heading if available
        if (position.coords.heading !== null && !isNaN(position.coords.heading)) {
          setDriverHeading(position.coords.heading);
        }

        // Send location update to server via WebSocket or HTTP fallback
        if (driverWsRef.current && driverWsRef.current.readyState === WebSocket.OPEN) {
          driverWsRef.current.send(JSON.stringify({
            type: 'location_update',
            lat: newLocation.lat,
            lng: newLocation.lng,
            heading: position.coords.heading,
            route_id: activeRoute?.id
          }));
        } else if (sessionToken) {
          // HTTP fallback when WebSocket is not available
          fetch(`${API_URL}/api/v1/delivery/drivers/location`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_token: sessionToken,
              lat: newLocation.lat,
              lng: newLocation.lng,
              route_id: activeRoute?.id
            })
          }).catch(err => console.error('Error sending location via HTTP:', err));
        }

        // Check for route deviation and recalculate if needed
        if (checkRouteDeviation(newLocation)) {
          // Debounce recalculation
          if (routeRecalcTimeoutRef.current) {
            clearTimeout(routeRecalcTimeoutRef.current);
          }
          routeRecalcTimeoutRef.current = setTimeout(() => {
            recalculateRoute();
          }, 3000); // Wait 3 seconds before recalculating to confirm deviation
        }
      },
      (error) => {
        console.error('Geolocation watch error:', error);
        if (error.code === error.PERMISSION_DENIED) {
          setGpsStatus('denied');
        } else {
          setGpsStatus('error');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 2000, // More frequent updates
        distanceFilter: 3  // Update every 3 meters
      }
    );

    // Also start polling as backup (some GPS spoofing extensions don't trigger watchPosition)
    if (locationPollingRef.current) {
      clearInterval(locationPollingRef.current);
    }

    locationPollingRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };

          // Use device heading if available from GPS
          const gpsHeading = position.coords.heading;
          if (gpsHeading !== null && !isNaN(gpsHeading)) {
            console.log('🧭 GPS Heading:', gpsHeading.toFixed(0) + '°');
            setDriverHeading(gpsHeading);
          }

          setDriverLocation(currentLocation => {
            // Only update if location actually changed
            if (!currentLocation ||
                Math.abs(currentLocation.lat - newLocation.lat) > 0.00001 ||
                Math.abs(currentLocation.lng - newLocation.lng) > 0.00001) {
              console.log('📍 Polling GPS Update:', newLocation.lat.toFixed(6), newLocation.lng.toFixed(6));

              // If no GPS heading, calculate from movement
              if (currentLocation && (gpsHeading === null || isNaN(gpsHeading))) {
                const distance = Math.sqrt(
                  Math.pow((newLocation.lat - currentLocation.lat) * 111000, 2) +
                  Math.pow((newLocation.lng - currentLocation.lng) * 111000 * Math.cos(currentLocation.lat * Math.PI / 180), 2)
                );
                if (distance > 3) { // Calculate heading if moved more than 3 meters
                  const calculatedHeading = calculateBearing(currentLocation, newLocation);
                  console.log('🧭 Calculated Heading:', calculatedHeading.toFixed(0) + '° (moved', distance.toFixed(1), 'm)');
                  setDriverHeading(calculatedHeading);
                }
              }

              // Send location to server via HTTP (polling backup)
              if (sessionToken) {
                fetch(`${API_URL}/api/v1/delivery/drivers/location`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    session_token: sessionToken,
                    lat: newLocation.lat,
                    lng: newLocation.lng,
                    route_id: activeRoute?.id
                  })
                }).catch(err => console.error('Error sending polling location:', err));
              }

              return { ...newLocation };
            }
            return currentLocation;
          });
        },
        (error) => {
          console.log('Polling GPS error:', error.message);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }, 3000); // Poll every 3 seconds
  };

  // Stop location tracking
  const stopLocationTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (locationPollingRef.current) {
      clearInterval(locationPollingRef.current);
      locationPollingRef.current = null;
    }
  };

  // Center map on driver location
  const centerOnDriver = () => {
    if (driverLocation) {
      setFollowingDriver(true);
      // The MapUpdater component will handle centering
    } else {
      toast.error('Ubicación no disponible');
      requestGpsPermission();
    }
  };
  
  // WebSocket connection for driver
  const driverWsRef = React.useRef(null);
  
  const connectDriverWebSocket = () => {
    if (!driver?.id) return;

    const wsUrl = `${getWsUrl()}/api/v1/delivery/ws/driver/${driver.id}`;
    
    console.log('Connecting driver WebSocket:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      driverWsRef.current = ws;
      
      ws.onopen = () => {
        console.log('Driver WebSocket connected');
        // Keep-alive ping
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Driver WS message:', data.type);
          
          if (data.type === 'new_order') {
            // New order available - play sound and show notification
            toast.info('📦 ¡Nuevo pedido disponible!');
            notifyNewOrder(data.order_number || 'Nuevo', data.address || 'Ver detalles');
            loadPendingOrders();
          } else if (data.type === 'chat_message') {
            // New chat message from customer - play sound
            setChatMessages(prev => [...prev, data.message]);
            toast.info('💬 Nuevo mensaje del cliente');
            notifyNewMessage(data.message?.sender_name || 'Cliente', data.message?.message || '');
          } else if (data.type === 'pong') {
            // Keep-alive response
          }
        } catch (e) {
          console.error('Error parsing WS message:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('Driver WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('Driver WebSocket closed');
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        // Reconnect after 5 seconds
        setTimeout(() => {
          if (isAuthenticated && driver?.id) {
            connectDriverWebSocket();
          }
        }, 5000);
      };
    } catch (error) {
      console.error('Error creating driver WebSocket:', error);
    }
  };
  
  // Connect WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated && driver?.id) {
      connectDriverWebSocket();
      // Also request notification permission
      requestNotificationPermission();
    }
    return () => {
      if (driverWsRef.current) {
        if (driverWsRef.current.pingInterval) {
          clearInterval(driverWsRef.current.pingInterval);
        }
        driverWsRef.current.close();
      }
    };
  }, [isAuthenticated, driver?.id]);

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1280;
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_SIZE || height > MAX_SIZE) {
        if (width > height) {
          height = Math.round(height * (MAX_SIZE / width));
          width = MAX_SIZE;
        } else {
          width = Math.round(width * (MAX_SIZE / height));
          height = MAX_SIZE;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      setProofPhoto(canvas.toDataURL('image/webp', 0.75));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  };

  const sendChatMessage = async (message, isPredefined = false) => {
    if (!currentDelivery) return;
    
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: currentDelivery.order_id,
          sender_type: 'driver',
          sender_id: driver.id,
          message,
          is_predefined: isPredefined
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, data]);
        toast.success('Mensaje enviado');
      }
    } catch (error) {
      toast.error('Error al enviar mensaje');
    }
  };

  // Open location in Google Maps
  const openInMaps = (location, address) => {
    if (!location) return;
    // Use coordinates for accuracy, fallback to address
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Open location in Waze
  const openInWaze = (location) => {
    if (!location) return;
    const url = `https://waze.com/ul?ll=${location.lat},${location.lng}&navigate=yes`;
    window.open(url, '_blank');
  };

  // Optimize route using OSRM
  const optimizeRoute = async () => {
    if (!activeRoute || activeRoute.status !== 'created') {
      toast.error('Solo puedes optimizar rutas antes de iniciar');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/routes/${activeRoute.id}/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_token: sessionToken,
          driver_location: driverLocation 
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.route) {
          setActiveRoute(data.route);
        }
        toast.success('¡Ruta optimizada!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al optimizar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate ETA for active route
  const recalculateETA = async () => {
    if (!activeRoute || !driverLocation) return;
    
    try {
      const res = await fetch(`${API_URL}/api/v1/delivery/routes/${activeRoute.id}/recalculate-eta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_token: sessionToken,
          lat: driverLocation.lat,
          lng: driverLocation.lng
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.orders) {
          setActiveRoute(prev => ({
            ...prev,
            orders: data.orders,
            estimated_duration_minutes: data.total_eta_minutes
          }));
        }
      }
    } catch (error) {
      console.error('Failed to recalculate ETA:', error);
    }
  };

  // Recalculate ETA when driver location changes significantly
  useEffect(() => {
    if (activeRoute && activeRoute.status === 'in_progress' && driverLocation) {
      const interval = setInterval(recalculateETA, 60000); // Every minute
      return () => clearInterval(interval);
    }
  }, [activeRoute?.status, driverLocation]);

  // Login Screen
  if (!isAuthenticated && !needsBranchSelection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Todo en Local</h1>
            <p className="text-gray-500 mt-1">App de Repartidores</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="tu@correo.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Iniciando...' : 'Iniciar Sesión'}
            </button>
          </form>

          {/* PWA Install on login screen - Android */}
          {showInstallBanner && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
              <img src="/delivery-icons/icon-72x72.png" alt="" className="w-10 h-10 rounded-lg flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-800">Instalar App</p>
                <p className="text-xs text-gray-500">Acceso rapido desde tu inicio</p>
              </div>
              <button onClick={handleInstallApp} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg flex-shrink-0">
                Instalar
              </button>
            </div>
          )}
          {/* PWA Install on login screen - iOS */}
          {showIosBanner && (
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-3">
              <div className="flex items-start gap-3">
                <img src="/delivery-icons/icon-72x72.png" alt="" className="w-10 h-10 rounded-lg flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800">Instalar App</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Toca <span className="inline-flex items-center mx-0.5 align-middle"><svg className="w-3.5 h-3.5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> <strong>Compartir</strong> y luego <strong>"Agregar a inicio"</strong>
                  </p>
                </div>
                <button onClick={dismissInstallBanner} className="p-0.5 text-gray-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Branch Selection Screen
  if (needsBranchSelection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Selecciona tu Sucursal</h1>
            <p className="text-gray-500 mt-1">¿Desde dónde trabajarás hoy?</p>
          </div>
          
          <div className="space-y-3">
            {branches.map(branch => (
              <button
                key={branch.id}
                onClick={() => handleSelectBranch(branch)}
                disabled={loading}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{branch.name}</p>
                    <p className="text-sm text-gray-500">{branch.address}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main App
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Toaster position="top-center" />

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="bg-blue-700 text-white px-4 py-3 flex items-center gap-3 z-[60] relative">
          <img src="/delivery-icons/icon-96x96.png" alt="" className="w-10 h-10 rounded-lg flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Instalar App de Repartidor</p>
            <p className="text-xs text-blue-200">Acceso directo desde tu pantalla de inicio</p>
          </div>
          <button onClick={handleInstallApp} className="px-3 py-1.5 bg-white text-blue-700 text-xs font-bold rounded-lg flex-shrink-0">
            Instalar
          </button>
          <button onClick={dismissInstallBanner} className="p-1 hover:bg-blue-600 rounded flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* iOS Install Instructions Banner */}
      {showIosBanner && (
        <div className="bg-gray-800 text-white px-4 py-3 z-[60] relative">
          <div className="flex items-start gap-3">
            <img src="/delivery-icons/icon-72x72.png" alt="" className="w-10 h-10 rounded-lg flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Instalar App de Repartidor</p>
              <p className="text-xs text-gray-300 mt-1">
                Toca el boton <span className="inline-flex items-center mx-0.5 align-middle"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> <strong>Compartir</strong> y luego <strong>"Agregar a inicio"</strong>
              </p>
            </div>
            <button onClick={dismissInstallBanner} className="p-1 hover:bg-gray-700 rounded flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* SW Update Banner */}
      {swUpdateAvailable && (
        <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center gap-3 z-[60] relative">
          <RefreshCw className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 text-sm font-medium">Nueva version disponible</p>
          <button onClick={handleSwUpdate} className="px-3 py-1.5 bg-white text-amber-700 text-xs font-bold rounded-lg flex-shrink-0">
            Actualizar
          </button>
        </div>
      )}

      {/* Header */}
      <header className="bg-blue-600 text-white px-4 py-3 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6" />
            <div>
              <h1 className="font-semibold">Todo en Local</h1>
              <p className="text-xs text-blue-200">{selectedBranch?.name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-20">
        {/* Orders View */}
        {view === 'orders' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Pedidos Pendientes</h2>
              <button
                onClick={loadPendingOrders}
                className="p-2 bg-white rounded-lg shadow-sm hover:bg-gray-50"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {activeRoute && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">Tienes una ruta activa</span>
                  </div>
                  <button
                    onClick={() => setView('route')}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg"
                  >
                    Ver Ruta
                  </button>
                </div>
              </div>
            )}

            {pendingOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay pedidos pendientes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingOrders.map(order => (
                  <div
                    key={order.id}
                    className={`bg-white rounded-xl shadow-sm border-2 transition-colors ${
                      selectedOrders.includes(order.id) ? 'border-blue-500' : 'border-transparent'
                    }`}
                  >
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => toggleOrderSelection(order.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">#{order.order_number || order.id.slice(0,8)}</p>
                          <p className="text-sm text-gray-500">{order.customer_name}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedOrders.includes(order.id) 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-gray-300'
                        }`}>
                          {selectedOrders.includes(order.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{order.delivery_address}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{order.customer_phone}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {order.payment_status === 'paid' ? (
                            <CreditCard className="w-4 h-4 text-green-500" />
                          ) : (
                            <Banknote className="w-4 h-4 text-orange-500" />
                          )}
                          <span className={order.payment_status === 'paid' ? 'text-green-600' : 'text-orange-600'}>
                            {order.payment_status === 'paid' ? 'Pagado' : 'Por cobrar'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-bold text-gray-900">${order.total?.toFixed(2)}</span>
                        <span className="text-xs text-gray-400">{order.items_count} productos</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedOrders.length > 0 && (
              <div className="fixed bottom-20 left-4 right-4">
                <button
                  onClick={createRoute}
                  disabled={loading}
                  className="w-full py-4 bg-blue-600 text-white font-semibold rounded-xl shadow-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Navigation className="w-5 h-5" />
                  Crear Ruta ({selectedOrders.length} pedidos)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Route View */}
        {view === 'route' && activeRoute && (
          <div className="flex flex-col">
            {/* Map - Fixed height */}
            <div className="bg-gray-200 relative sticky top-0 z-10" style={{ height: '65vh', minHeight: '380px' }}>
              <MapContainer
                center={driverLocation || [20.659698, -103.349609]}
                zoom={17}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />

                {/* Route polyline */}
                {routeGeometry.length > 0 && (
                  <Polyline
                    positions={routeGeometry}
                    color="#3B82F6"
                    weight={5}
                    opacity={0.9}
                  />
                )}

                {/* Driver navigation arrow - using custom component for real-time updates */}
                <DriverMarker position={driverLocation} heading={driverHeading} />

                {/* Delivery markers */}
                {activeRoute.orders.map(order => (
                  order.delivery_location && (
                    <Marker
                      key={order.order_id}
                      position={[order.delivery_location.lat, order.delivery_location.lng]}
                      icon={order.status === 'delivered' || order.status === 'failed' ? completedIcon : deliveryIcon}
                    >
                      <Popup>
                        <b>#{order.sequence}</b> {order.customer_name}<br />
                        {order.delivery_address}
                      </Popup>
                    </Marker>
                  )
                ))}

                <MapUpdater
                  center={driverLocation ? [driverLocation.lat, driverLocation.lng] : null}
                  zoom={17}
                  heading={driverHeading}
                  followDriver={followingDriver && activeRoute.status === 'in_progress'}
                />
                <MapDragHandler onDrag={() => setFollowingDriver(false)} />
              </MapContainer>

              {/* Floating button to center on driver - Uber style */}
              <button
                onClick={centerOnDriver}
                className={`absolute bottom-4 right-4 z-[1000] w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
                  followingDriver
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="Centrar en mi ubicación"
              >
                {gpsStatus === 'requesting' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : gpsStatus === 'denied' || gpsStatus === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <Navigation2 className={`w-5 h-5 ${followingDriver ? '' : 'opacity-60'}`} />
                )}
              </button>

              {/* Recalculate route button */}
              {activeRoute.status === 'in_progress' && (
                <button
                  onClick={() => recalculateRoute(true)}
                  className="absolute bottom-4 right-20 z-[1000] w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all active:scale-95"
                  title="Recalcular ruta desde mi ubicación"
                >
                  <RefreshCw className="w-5 h-5 text-gray-700" />
                </button>
              )}

              {/* GPS Status indicator */}
              {(gpsStatus === 'denied' || gpsStatus === 'error') && (
                <div className="absolute bottom-20 right-4 z-[1000]">
                  <button
                    onClick={requestGpsPermission}
                    className="bg-red-500 text-white text-xs px-3 py-2 rounded-lg shadow-lg flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    GPS desactivado - Toca para activar
                  </button>
                </div>
              )}

              {/* Floating info overlay */}
              {activeRoute.status === 'in_progress' && (
                <div className="absolute top-4 left-4 right-4 z-[1000]">
                  <div className="bg-white/95 backdrop-blur rounded-xl shadow-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Próxima entrega</p>
                        <p className="font-semibold text-gray-900">
                          {activeRoute.orders.find(o => o.status !== 'delivered' && o.status !== 'failed')?.customer_name || 'Completado'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Restantes</p>
                        <p className="font-bold text-blue-600">
                          {activeRoute.total_orders - activeRoute.completed_orders} de {activeRoute.total_orders}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Route Info Header */}
            <div className="p-4 bg-white border-b shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-bold text-lg text-gray-900">Mi Ruta</h2>
                  <p className="text-sm text-gray-500">
                    {activeRoute.completed_orders} de {activeRoute.total_orders} entregas completadas
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {activeRoute.status === 'created' && (
                    <>
                      <button
                        onClick={optimizeRoute}
                        disabled={loading}
                        className="px-3 py-2 bg-blue-100 text-blue-700 font-medium rounded-lg flex items-center gap-1 text-sm"
                      >
                        <Navigation className="w-4 h-4" />
                        Optimizar
                      </button>
                      <button
                        onClick={startRoute}
                        disabled={loading}
                        className="px-4 py-2 bg-green-500 text-white font-medium rounded-lg flex items-center gap-2"
                      >
                        <Play className="w-4 h-4" />
                        Iniciar
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Barra de progreso visual */}
              <div className="mb-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
                    style={{ width: `${(activeRoute.completed_orders / activeRoute.total_orders) * 100}%` }}
                  />
                </div>
              </div>

              {/* ETA Total y distancia */}
              {(activeRoute.estimated_duration_minutes || activeRoute.total_distance_km) && (
                <div className="flex items-center gap-4 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  {activeRoute.estimated_duration_minutes > 0 && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-xs text-gray-400">Tiempo est.</p>
                        <p className="font-semibold text-gray-700">~{activeRoute.estimated_duration_minutes} min</p>
                      </div>
                    </div>
                  )}
                  {activeRoute.total_distance_km > 0 && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-xs text-gray-400">Distancia</p>
                        <p className="font-semibold text-gray-700">{activeRoute.total_distance_km?.toFixed(1)} km</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 ml-auto">
                    <Package className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Restantes</p>
                      <p className="font-semibold text-gray-700">{activeRoute.total_orders - activeRoute.completed_orders}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Orders List - Sin scroll separado, integrado al scroll general */}
            <div className="p-4 space-y-3 pb-24">
              {activeRoute.orders.map((order, index) => {
                // Detectar si es la próxima entrega (primera no completada ni fallida)
                const isNextDelivery = activeRoute.status === 'in_progress' &&
                  order.status !== 'delivered' &&
                  order.status !== 'failed' &&
                  !activeRoute.orders.slice(0, index).some(o => o.status !== 'delivered' && o.status !== 'failed');

                return (
                <div
                  key={order.order_id}
                  className={`bg-white rounded-2xl shadow-sm p-4 transition-all duration-300 ${
                    order.status === 'delivered'
                      ? 'border border-green-200 opacity-60'
                      : order.status === 'failed'
                      ? 'border border-red-200 opacity-60'
                      : isNextDelivery
                      ? 'border-2 border-blue-500 shadow-lg shadow-blue-100 ring-2 ring-blue-100'
                      : 'border border-gray-200'
                  }`}
                >
                  {/* Indicador de próxima entrega */}
                  {isNextDelivery && (
                    <div className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3">
                      📍 PRÓXIMA ENTREGA
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-600' :
                        order.status === 'failed' ? 'bg-red-100 text-red-600' :
                        isNextDelivery ? 'bg-blue-500 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {order.status === 'delivered' ? <Check className="w-4 h-4" /> :
                         order.status === 'failed' ? <X className="w-4 h-4" /> :
                         order.sequence}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900">{order.customer_name}</p>
                        <p className="text-xs text-gray-400">Pedido #{order.order_number}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                      order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                      order.status === 'failed' ? 'bg-red-100 text-red-700' :
                      order.status === 'nearby' ? 'bg-orange-100 text-orange-700' :
                      order.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {order.status === 'delivered' ? '✓ Entregado' :
                       order.status === 'failed' ? '✗ Fallido' :
                       order.status === 'nearby' ? '📍 Cerca' :
                       order.status === 'in_transit' ? '🚗 En camino' :
                       order.status === 'picked_up' ? '📦 Recogido' :
                       'Asignado'}
                    </span>
                  </div>

                  {/* Dirección */}
                  <div className="flex items-start gap-2 text-sm text-gray-600 mb-3 bg-gray-50 p-2 rounded-lg">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                    <span className="line-clamp-2">{order.delivery_address}</span>
                  </div>

                  {/* Info row: ETA, teléfono, pago */}
                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className="flex items-center gap-3">
                      {order.eta_minutes && order.status !== 'delivered' && order.status !== 'failed' && (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">~{order.eta_minutes} min</span>
                        </div>
                      )}
                      <a href={`tel:${order.customer_phone}`} className="flex items-center gap-1 text-gray-500 hover:text-purple-600">
                        <Phone className="w-4 h-4" />
                        <span>{order.customer_phone}</span>
                      </a>
                    </div>
                    <div className="flex items-center gap-1">
                      {order.payment_status === 'paid' ? (
                        <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                          <CreditCard className="w-3 h-3" />
                          Pagado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-bold">
                          <Banknote className="w-3 h-3" />
                          Cobrar ${order.total?.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Botones de navegación - más grandes y accesibles */}
                  {order.status !== 'delivered' && order.status !== 'failed' && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <a
                        href={`tel:${order.customer_phone}`}
                        className="py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors"
                      >
                        <Phone className="w-5 h-5" />
                        <span className="text-xs font-medium">Llamar</span>
                      </a>
                      {order.delivery_location && (
                        <>
                          <button
                            onClick={() => openInMaps(order.delivery_location, order.delivery_address)}
                            className="py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors"
                          >
                            <Navigation className="w-5 h-5" />
                            <span className="text-xs font-medium">Maps</span>
                          </button>
                          <button
                            onClick={() => openInWaze(order.delivery_location)}
                            className="py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors"
                          >
                            <MapPin className="w-5 h-5" />
                            <span className="text-xs font-medium">Waze</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Proof photo for delivered orders */}
                  {order.status === 'delivered' && order.proof_photo_url && (
                    <div className="mb-3">
                      <button
                        onClick={() => setSelectedHistoryItem({
                          ...order,
                          delivery_address: order.delivery_address,
                          total: order.total,
                          completed_at: order.delivered_at,
                          created_at: order.delivered_at
                        })}
                        className="flex items-center gap-2 text-sm text-green-600 bg-green-50 hover:bg-green-100 px-3 py-2 rounded-lg transition-colors"
                      >
                        <Camera className="w-4 h-4" />
                        <span className="font-medium">Ver evidencia de entrega</span>
                      </button>
                    </div>
                  )}

                  {/* Botones de acción de entrega - más prominentes */}
                  {order.status !== 'delivered' && order.status !== 'failed' && activeRoute.status === 'in_progress' && (
                    <div className="space-y-3">
                      {order.status === 'assigned' && (
                        <button
                          onClick={() => updateOrderStatus(order.order_id, 'in_transit')}
                          className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all shadow-md shadow-blue-200"
                        >
                          🚗 Iniciar entrega
                        </button>
                      )}
                      {order.status === 'in_transit' && !isNextDelivery && (
                        <button
                          onClick={() => updateOrderStatus(order.order_id, 'nearby')}
                          className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-md shadow-orange-200"
                        >
                          📍 Estoy cerca
                        </button>
                      )}

                      {/* Swipe to complete - solo para la próxima entrega cuando está cerca */}
                      {isNextDelivery && (order.status === 'nearby' || order.status === 'in_transit') && (
                        <div
                          className="relative h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl overflow-hidden shadow-lg shadow-green-200"
                          onTouchStart={(e) => handleSwipeStart(order.order_id, e)}
                          onTouchMove={(e) => handleSwipeMove(order.order_id, e)}
                          onTouchEnd={() => handleSwipeEnd(order.order_id, order)}
                          onMouseDown={(e) => handleSwipeStart(order.order_id, e)}
                          onMouseMove={(e) => swipeProgress[order.order_id]?.swiping && handleSwipeMove(order.order_id, e)}
                          onMouseUp={() => handleSwipeEnd(order.order_id, order)}
                          onMouseLeave={() => handleSwipeEnd(order.order_id, order)}
                        >
                          {/* Progress fill */}
                          <div
                            className="absolute inset-0 bg-green-400 transition-all"
                            style={{
                              width: `${Math.min((swipeProgress[order.order_id]?.deltaX || 0) / 2.5, 100)}%`,
                              opacity: 0.5
                            }}
                          />
                          {/* Slider thumb */}
                          <div
                            className="absolute top-2 bottom-2 left-2 w-12 bg-white rounded-xl flex items-center justify-center shadow-md transition-transform"
                            style={{
                              transform: `translateX(${Math.min(swipeProgress[order.order_id]?.deltaX || 0, 250)}px)`
                            }}
                          >
                            <ChevronRight className="w-6 h-6 text-green-600" />
                          </div>
                          {/* Text */}
                          <div className="absolute inset-0 flex items-center justify-center text-white font-semibold pointer-events-none">
                            {(swipeProgress[order.order_id]?.deltaX || 0) > 150
                              ? '¡Soltar para completar!'
                              : 'Desliza para completar →'}
                          </div>
                        </div>
                      )}

                      {/* Botones secundarios */}
                      {(order.status === 'nearby' || order.status === 'in_transit') && (
                        <div className="flex gap-2">
                          {!isNextDelivery && (
                            <button
                              onClick={() => {
                                setCurrentDelivery(order);
                                setShowPhotoModal(true);
                              }}
                              className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
                            >
                              <CheckCircle className="w-5 h-5" />
                              Completar
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setCurrentDelivery(order);
                              setShowProblemModal(true);
                            }}
                            className="px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors"
                            title="Reportar problema"
                          >
                            <AlertTriangle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              setCurrentDelivery(order);
                              setView('chat');
                            }}
                            className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors"
                            title="Chat con cliente"
                          >
                            <MessageCircle className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* History View */}
        {view === 'history' && (
          <div className="p-4 space-y-4 pb-24">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Historial de Entregas</h2>
              <button
                onClick={loadHistory}
                className="p-2 bg-white rounded-lg shadow-sm hover:bg-gray-50"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${historyLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {historyLoading && historyDeliveries.length === 0 ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
                <p className="text-gray-500">Cargando historial...</p>
              </div>
            ) : historyDeliveries.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay entregas completadas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historyDeliveries.map(delivery => {
                  const date = delivery.completed_at || delivery.created_at;
                  const formattedDate = date ? new Date(date).toLocaleDateString('es-MX', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                  }) : '';
                  const isDelivered = !delivery.problem_type;

                  return (
                    <button
                      key={delivery.id}
                      onClick={() => setSelectedHistoryItem(delivery)}
                      className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-left hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">#{delivery.order_id?.slice(0, 8)}</p>
                          <p className="text-sm text-gray-500">{delivery.customer_name}</p>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                          isDelivered
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {isDelivered ? 'Entregado' : 'Problema'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{delivery.delivery_address}</span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{formattedDate}</span>
                        <span className="font-semibold text-gray-900">${delivery.total?.toFixed(2)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* History Detail Modal */}
        {selectedHistoryItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex: 9999 }}>
            <div className="bg-white w-full sm:rounded-2xl sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-2xl">
              <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between z-10">
                <h3 className="font-semibold text-gray-900">Detalle de Entrega</h3>
                <button onClick={() => setSelectedHistoryItem(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    !selectedHistoryItem.problem_type ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {!selectedHistoryItem.problem_type
                      ? <CheckCircle className="w-5 h-5 text-green-600" />
                      : <XCircle className="w-5 h-5 text-red-600" />}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {!selectedHistoryItem.problem_type ? 'Entrega Exitosa' : 'Entrega con Problema'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedHistoryItem.completed_at
                        ? new Date(selectedHistoryItem.completed_at).toLocaleDateString('es-MX', {
                            weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                          })
                        : ''}
                    </p>
                  </div>
                </div>

                {/* Customer & Order Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Cliente</span>
                    <span className="text-sm font-medium text-gray-900">{selectedHistoryItem.customer_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Pago</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedHistoryItem.payment_method === 'cash_on_delivery' || selectedHistoryItem.payment_method === 'cash_on_pickup'
                        ? 'Efectivo' : selectedHistoryItem.payment_method === 'card_on_pickup' ? 'Tarjeta' : selectedHistoryItem.payment_method}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Total</span>
                    <span className="text-lg font-bold text-gray-900">${selectedHistoryItem.total?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{selectedHistoryItem.delivery_address}</span>
                  </div>
                </div>

                {/* Notes */}
                {selectedHistoryItem.delivery_notes && (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="text-xs font-medium text-blue-600 mb-1">Notas de entrega</p>
                    <p className="text-sm text-blue-900">{selectedHistoryItem.delivery_notes}</p>
                  </div>
                )}

                {/* Problem details */}
                {selectedHistoryItem.problem_type && (
                  <div className="bg-red-50 rounded-xl p-4">
                    <p className="text-xs font-medium text-red-600 mb-1">Problema reportado</p>
                    <p className="text-sm font-medium text-red-900">
                      {selectedHistoryItem.problem_type === 'customer_absent' ? 'Cliente ausente' :
                       selectedHistoryItem.problem_type === 'wrong_address' ? 'Direccion incorrecta' :
                       selectedHistoryItem.problem_type === 'product_damaged' ? 'Producto danado' :
                       selectedHistoryItem.problem_type === 'customer_rejected' ? 'Cliente rechazo' :
                       selectedHistoryItem.problem_type === 'access_denied' ? 'Sin acceso' :
                       selectedHistoryItem.problem_type}
                    </p>
                    {selectedHistoryItem.problem_notes && (
                      <p className="text-sm text-red-800 mt-1">{selectedHistoryItem.problem_notes}</p>
                    )}
                  </div>
                )}

                {/* Proof Photo */}
                {selectedHistoryItem.proof_photo_url && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Evidencia de entrega</p>
                    <img
                      src={selectedHistoryItem.proof_photo_url.startsWith('http')
                        ? selectedHistoryItem.proof_photo_url
                        : `${API_URL}${selectedHistoryItem.proof_photo_url}`}
                      alt="Evidencia de entrega"
                      className="w-full rounded-xl border border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chat View */}
        {view === 'chat' && currentDelivery && (
          <div className="h-full flex flex-col">
            <div className="p-4 bg-white border-b">
              <div className="flex items-center gap-3">
                <button onClick={() => setView('route')} className="p-2 -ml-2">
                  <X className="w-5 h-5" />
                </button>
                <div>
                  <p className="font-medium">{currentDelivery.customer_name}</p>
                  <p className="text-sm text-gray-500">Chat con cliente</p>
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 space-y-2 overflow-auto">
              {chatMessages.map(msg => (
                <div
                  key={msg.id}
                  className={`max-w-[80%] p-3 rounded-xl ${
                    msg.sender_type === 'driver' 
                      ? 'bg-blue-500 text-white ml-auto' 
                      : 'bg-gray-200 text-gray-900'
                  }`}
                >
                  {msg.message}
                </div>
              ))}
            </div>

            {/* Input de texto libre para el repartidor */}
            <div className="p-4 bg-white border-t space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && customMessage.trim() && sendChatMessage(customMessage.trim())}
                  placeholder="Escribe un mensaje..."
                  className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => {
                    if (customMessage.trim()) {
                      sendChatMessage(customMessage.trim());
                      setCustomMessage('');
                    }
                  }}
                  disabled={!customMessage.trim()}
                  className="p-2 bg-blue-500 text-white rounded-full disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              
              <div>
                <p className="text-xs text-gray-500 mb-2">Mensajes rápidos:</p>
                <div className="flex flex-wrap gap-2">
                  {predefinedMessages.map((msg, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendChatMessage(msg, true)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200"
                    >
                      {msg}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      {isAuthenticated && view !== 'chat' && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
          <div className="flex items-center justify-around">
            <button
              onClick={() => setView('orders')}
              className={`flex flex-col items-center gap-1 ${view === 'orders' ? 'text-blue-600' : 'text-gray-400'}`}
            >
              <Package className="w-6 h-6" />
              <span className="text-xs">Pedidos</span>
            </button>
            <button
              onClick={() => setView('route')}
              disabled={!activeRoute}
              className={`flex flex-col items-center gap-1 ${view === 'route' ? 'text-blue-600' : 'text-gray-400'} ${!activeRoute ? 'opacity-50' : ''}`}
            >
              <Navigation className="w-6 h-6" />
              <span className="text-xs">Mi Ruta</span>
            </button>
            <button
              onClick={() => { setView('history'); if (historyDeliveries.length === 0) loadHistory(); }}
              className={`flex flex-col items-center gap-1 ${view === 'history' ? 'text-blue-600' : 'text-gray-400'}`}
            >
              <Clock className="w-6 h-6" />
              <span className="text-xs">Historial</span>
            </button>
          </div>
        </nav>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Completar Entrega</h3>
              <button onClick={() => setShowPhotoModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Foto de comprobante (obligatoria):</p>
                {proofPhoto ? (
                  <div className="relative">
                    <img src={proofPhoto} alt="Comprobante" className="w-full rounded-lg" />
                    <button
                      onClick={() => setProofPhoto(null)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-8 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center gap-2 text-gray-500 hover:border-blue-500 hover:text-blue-500"
                  >
                    <Camera className="w-8 h-8" />
                    <span>Tomar foto</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Notas (opcional):</label>
                <textarea
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Ej: Dejado con el vecino"
                />
              </div>
              <button
                onClick={completeDelivery}
                disabled={loading || !proofPhoto}
                className="w-full py-3 bg-green-500 text-white font-semibold rounded-xl disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Confirmar Entrega'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Problem Modal */}
      {showProblemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-red-600">Reportar Problema</h3>
              <button onClick={() => setShowProblemModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Tipo de problema:</p>
                <div className="space-y-2">
                  {[
                    { value: 'customer_absent', label: 'Cliente ausente' },
                    { value: 'wrong_address', label: 'Dirección incorrecta' },
                    { value: 'product_damaged', label: 'Producto dañado' },
                    { value: 'customer_rejected', label: 'Cliente rechazó el pedido' },
                    { value: 'access_denied', label: 'No se pudo acceder' },
                    { value: 'other', label: 'Otro' }
                  ].map(option => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                        problemType === option.value ? 'border-red-500 bg-red-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name="problem"
                        value={option.value}
                        checked={problemType === option.value}
                        onChange={(e) => setProblemType(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        problemType === option.value ? 'border-red-500 bg-red-500' : 'border-gray-300'
                      }`}>
                        {problemType === option.value && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Descripción (opcional):</label>
                <textarea
                  value={problemNotes}
                  onChange={(e) => setProblemNotes(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Detalles adicionales..."
                />
              </div>
              <button
                onClick={reportProblem}
                disabled={loading || !problemType}
                className="w-full py-3 bg-red-500 text-white font-semibold rounded-xl disabled:opacity-50"
              >
                {loading ? 'Enviando...' : 'Reportar Problema'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryApp;
